import React, { useState } from 'react';
import {
  ShieldCheck,
  Lock,
  Cpu,
  FileKey,
  FileCheck,
  Server,
  FileText,
  Download,
  CheckCircle2,
  AlertTriangle,
  X,
  ExternalLink,
  Trash2,
  Eye,
  Copy,
  Check
} from 'lucide-react';
import { useGst } from '../context/GstContext';

interface SecurityTrustModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenPurge?: () => void;
}

export const SecurityTrustModal: React.FC<SecurityTrustModalProps> = ({
  isOpen,
  onClose,
  onOpenPurge
}) => {
  const { clientId, clientLegalName, user, financialYear, taxPeriod } = useGst();
  const [activeTab, setActiveTab] = useState<'architecture' | 'legal' | 'audit'>('architecture');
  const [copiedDoc, setCopiedDoc] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleCopyLink = (name: string, path: string) => {
    navigator.clipboard.writeText(window.location.origin + path);
    setCopiedDoc(name);
    setTimeout(() => setCopiedDoc(null), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-cyan-500/30 bg-[#0e1015] text-[#f7f7f8] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/[0.08] px-6 py-4 bg-gradient-to-r from-cyan-950/40 via-[#12151c] to-[#0e1015]">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center text-cyan-400 shadow-md shadow-cyan-500/20">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-white tracking-wide">
                  Enterprise Data Shield & Security Governance
                </h2>
                <span className="rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-mono font-bold uppercase">
                  Active & Guarded
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Sovereign Multi-Tenant Isolation & Zero-Data-Leakage Framework &bull; <span className="font-mono text-cyan-300">gst.verveai.co</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl border border-white/10 p-2 text-slate-400 hover:text-white hover:bg-white/5 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-white/[0.06] bg-[#12141a] px-6 gap-2">
          <button
            onClick={() => setActiveTab('architecture')}
            className={`py-3 px-4 text-xs font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'architecture'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Server className="h-4 w-4" />
            <span>Zero-Leakage Architecture</span>
          </button>
          <button
            onClick={() => setActiveTab('legal')}
            className={`py-3 px-4 text-xs font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'legal'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>Legal Contracts (M-NDA & DPA)</span>
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-3 px-4 text-xs font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'audit'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <FileKey className="h-4 w-4" />
            <span>Audit & Data Purge</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-xs text-slate-300">
          {activeTab === 'architecture' && (
            <div className="space-y-6">
              {/* Tenant Status Card */}
              <div className="rounded-2xl border border-cyan-500/20 bg-cyan-950/20 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 font-medium">Assigned Organization / Tenant:</span>
                  <span className="font-bold text-white text-sm">{clientLegalName || 'Johalibash & Company'}</span>
                </div>
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <span className="text-slate-400">Tenant Client ID:</span>
                  <span className="text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-500/30">
                    {clientId || 'CLIENT_ACTIVE'}
                  </span>
                </div>
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <span className="text-slate-400">Isolated Storage Partition:</span>
                  <span className="text-slate-200">gs://verveailabs.firebasestorage.app/clients/{clientId || '{tenantId}'}/gst/</span>
                </div>
              </div>

              {/* Security Pillars Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pillar 1: Encryption */}
                <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-white font-semibold">
                    <Lock className="h-4 w-4 text-cyan-400" />
                    <span>Dual-Layer Cryptographic Encryption</span>
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    All data in transit is protected using <strong className="text-white">TLS 1.3 / HTTPS</strong> with HSTS. All uploaded invoices, extracted JSON tables, and database entries are encrypted at rest using hardware-grade <strong className="text-white">AES-256</strong>.
                  </p>
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>AES-256 At Rest &bull; TLS 1.3 In Transit</span>
                  </div>
                </div>

                {/* Pillar 2: Zero AI Training */}
                <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-white font-semibold">
                    <Cpu className="h-4 w-4 text-cyan-400" />
                    <span>Zero Data Retention AI Fabric</span>
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    Multimodal invoice extraction operates strictly under <strong className="text-white">Google Cloud Vertex AI Enterprise SLAs</strong>. Client financial transactions are processed in memory and <strong className="text-white">never retained or used to train AI models</strong>.
                  </p>
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>Zero Model Training &bull; 0% AI Retention</span>
                  </div>
                </div>

                {/* Pillar 3: Tenant Air-Gapping */}
                <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-white font-semibold">
                    <Server className="h-4 w-4 text-cyan-400" />
                    <span>Multi-Tenant Kernel Isolation</span>
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    Access is governed at the database kernel via declarative security rules. No tenant or unauthorized user can list, read, or infer another organization's invoices or returns.
                  </p>
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-mono text-cyan-300 bg-cyan-950/40 px-2 py-0.5 rounded border border-cyan-500/20">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>isGstAuthorized(clientId) Enforced</span>
                  </div>
                </div>

                {/* Pillar 4: In-Browser SHA-256 */}
                <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-white font-semibold">
                    <FileCheck className="h-4 w-4 text-cyan-400" />
                    <span>In-Browser SHA-256 Digesting</span>
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    Every file uploaded to the vault is digested using the browser's native <code className="text-cyan-300 font-mono">crypto.subtle</code> SHA-256 engine to prevent duplicate submissions and guarantee tamper-proof file authenticity.
                  </p>
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-mono text-cyan-300 bg-cyan-950/40 px-2 py-0.5 rounded border border-cyan-500/20">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>Client-Side SHA-256 Checksums</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'legal' && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                      <FileText className="h-4 w-4 text-cyan-400" />
                      <span>Master Mutual Non-Disclosure Agreement (M-NDA)</span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      Covers trade secrets, vendor non-circumvention, financial confidentiality, and non-disclosure obligations between Verve AI Labs and the Client.
                    </p>
                  </div>
                  <span className="shrink-0 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-2.5 py-1 text-[10px] font-mono font-bold">
                    READY TO SIGN
                  </span>
                </div>
                <div className="rounded-xl bg-[#0b0c0e] p-3 text-[11px] font-mono text-slate-400 space-y-1">
                  <div>Governing Law: Republic of India (Bangalore Jurisdiction)</div>
                  <div>Confidentiality Term: 5 Years post-disclosure</div>
                  <div>Scope: Financial Registers, Invoices, Inward ITC, Tax Filings</div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-cyan-400" />
                      <span>Enterprise Data Protection Addendum (DPA)</span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      Defines Client as the exclusive Data Controller and Verve AI Labs as Processor. Guarantees 100% Client ownership and a 36-hour breach notification SLA.
                    </p>
                  </div>
                  <span className="shrink-0 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 text-[10px] font-mono font-bold">
                    BINDING DPA
                  </span>
                </div>
                <div className="rounded-xl bg-[#0b0c0e] p-3 text-[11px] font-mono text-slate-400 space-y-1">
                  <div>Controller: Johalibash & Company (100% Data Owner)</div>
                  <div>Processor: Verve AI Labs Private Limited</div>
                  <div>AI Guarantee: Absolute prohibition against model training</div>
                  <div>Purge SLA: Cryptographic deletion within 7 days upon demand</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-5 space-y-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <FileKey className="h-4 w-4 text-cyan-400" />
                  <span>Ministry of Corporate Affairs (MCA) Audit Trail</span>
                </h3>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Every extraction, human edit, 2B match, and return export generates an immutable entry in the document's audit ledger, satisfying statutory electronic audit requirements under Rule 3(1) of the Companies (Accounts) Rules.
                </p>
              </div>

              {/* Data Purge Action Box */}
              <div className="rounded-2xl border border-rose-500/30 bg-rose-950/20 p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-rose-300 flex items-center gap-2">
                      <Trash2 className="h-4 w-4" />
                      <span>Client Data Purge & Right to Be Forgotten</span>
                    </h3>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      You maintain the unconditional right to erase all uploaded invoices and parsed tables for any tax period. Purging permanently deletes Cloud Storage files and generates an instant, signed <strong className="text-white">Certificate of Data Destruction</strong>.
                    </p>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    onClick={() => {
                      onClose();
                      if (onOpenPurge) onOpenPurge();
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs transition shadow-lg shadow-rose-600/20"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Open Period Data Purge Console</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-white/[0.08] px-6 py-4 bg-[#12141a]">
          <div className="flex items-center gap-2 text-slate-400 text-[11px]">
            <Lock className="h-3.5 w-3.5 text-cyan-400" />
            <span>Encrypted Session &bull; ISO 27001 & SOC 2 Aligned</span>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-black font-bold text-xs transition"
          >
            Acknowledge & Close
          </button>
        </div>
      </div>
    </div>
  );
};
